import { css } from "@emotion/react"
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  ChangeEvent,
} from "react"
import tw from "twin.macro"
import { LocalStorageClient } from "../../../clients/LocalStorageClient"
import { ConfirmZipContext } from "../../../contexts/ConfirmZip"
import {
  closeConfirmZipModal,
  openConfirmZipModal,
} from "../../../contexts/ConfirmZip/actions"
import { LanguageContext } from "../../../contexts/Language"
import { LocationContext } from "../../../contexts/Location"
import {
  setMapSearchZip,
  setRadius,
  setSelectedDealer,
} from "../../../contexts/Location/actions"
import { useTealiumContext } from "../../../contexts/Tealium"
import useTealiumEvent from "../../../hooks/Tealium/useTealiumEvent"
import useDealers from "../../../hooks/useDealers"
import useDealerSearch from "../../../hooks/Tealium/useDealerSearch"
import { Card } from "../../atoms/Card"
import Icon from "../../atoms/Icon"
import { Dealer } from "../../molecules/Dealer"
import { DealerMap } from "../../molecules/DealerMap"
import { LocationSelector } from "../../molecules/LocationSelector"
import { DealersNearYouProps } from "./DealersNearYou.d"
import { PilotDealer } from "../../molecules/Dealer"
import { scrollIntoView } from "seamless-scroll-polyfill"
import useViewportSize from "../../../hooks/useViewportSize"
import usePagination from "../../../hooks/usePagination"
import Paginate from "../../molecules/DealerMap/Pagination"
import {
  getIndexOfNestedArrays,
  parseDisclaimerBlocks,
  sliceArrayIntoArrays,
} from "../../../helpers"
import { ConfirmZipModal } from "../../molecules/ConfirmZipModal"
import { toggleDisclaimersModal } from "../../../contexts/Disclaimers/actions"
import { DisclaimersContext } from "../../../contexts/Disclaimers"
import { RadiusSelector } from "../../molecules/RadiusSelector"

/**
 * The "dealers near you" section that can allows CTAs to be customized -- specifically around the P&S area.
 * @author Cody
 *
 * @returns <section></section>
 *
 */

const DealersNearYou: React.FC<DealersNearYouProps> = section => {
  const {
    dealerCtaLink,
    dealerCtaText,
    type,
    ctaAnalyticsId = null,
    offerType = null,
    pilotDealers = "",
    pilotPhoneNumber = "",
    carCareTopic = null,
  } = section
  const { _ } = useContext(LanguageContext)

  // Location context
  const [
    {
      zip,
      radius,
      value,
      dealersLoading,
      dealers,
      selectedDealer,
      latLng,
      mapSearchZip,
    },
    dispatch,
  ] = useContext(LocationContext)
  const [filteredDealers, setFilteredDealers] = useState<
    DealerType[] | undefined
  >([])

  // Defines map dimensions
  const mapOffset = 160
  const mapHeight = 700

  // Which radius options are available to the user
  const radiusOptions = [25, 50, 100, 250, 500, 600]

  // Controls the mobile tab state
  const [view, setView] = useState(0)

  // Applies when a dealer is selected by the user on the map
  const activeStyle = tw`border-red-400`

  const heading =
    type === "default"
      ? _("Toyota Dealers Near You")
      : type === "tact"
        ? _("Apply at Toyota Dealers Near You")
        : ""

  const handleAutoIncreaseRadius = () => {
    const nextIndex = radiusOptions.indexOf(radius) + 1
    if (nextIndex < radiusOptions.length) {
      dispatch(setRadius(radiusOptions[nextIndex]))
    }
  }

  // Tealium
  const { trackTealEvent } = useTealiumEvent()
  const {
    tealPageData,
    refineInAction,
    setRefineInAction,
    lifestyleLanderTealData,
  } = useTealiumContext()
  const pageload = useRef(true)

  //loading for offers page only
  useEffect(() => {
    if (location.pathname == "/offers/") {
      trackTealEvent({
        page_type: tealPageData?.page_type,
        search_results: dealers?.length,
        dealer_code: dealers?.map(dealer => dealer.DealerCode),
        dealer_name: dealers?.map(dealer => dealer.Name),
      })
    }
  }, [])

  useEffect(() => {
    const pilotDealerCodes = pilotDealers?.replaceAll(" ", "")?.split(",")
    if (pilotDealerCodes?.length > 0 && pilotDealerCodes[0]) {
      let pilotDealers = dealers?.filter(dealer =>
        pilotDealerCodes.includes(dealer?.DealerCode),
      )
      pilotDealers?.length === 0 && handleAutoIncreaseRadius()
      setFilteredDealers(pilotDealers)

      // Tealium
      if (
        !pageload.current &&
        refineInAction &&
        refineInAction.inProgress === true &&
        setRefineInAction
      ) {
        trackTealEvent({
          tealium_event: "refinement",
          page_type: tealPageData?.page_type,
          search_results: pilotDealers?.length,
          refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Miles|${radius}`,
          dealer_code: pilotDealers?.map(dealer => dealer.DealerCode),
          dealer_name: pilotDealers?.map(dealer => dealer.Name),
          ...(carCareTopic && { vehicle_category: [carCareTopic] }),
        })
        pageload.current = false
        setRefineInAction({ inProgress: false })
      }
    } else if (dealerCtaLink === "tact") {
      let tactDealers = dealers?.filter(dealer => dealer.TactUrl)
      tactDealers?.length === 0 && handleAutoIncreaseRadius()
      setFilteredDealers(tactDealers)

      // Tealium
      if (
        !pageload.current &&
        refineInAction &&
        refineInAction.inProgress === true &&
        setRefineInAction
      ) {
        trackTealEvent({
          tealium_event: "refinement",
          page_type: tealPageData?.page_type,
          search_results: tactDealers?.length,
          refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Miles|${radius}`,
          dealer_code: tactDealers?.map(dealer => dealer.DealerCode),
          dealer_name: tactDealers?.map(dealer => dealer.Name),
        })
        pageload.current = false
        setRefineInAction({ inProgress: false })
      }
    } else if (dealerCtaLink === "tires") {
      let dealersWithTires = dealers?.filter(dealer => dealer.DealerTireUrl)
      setFilteredDealers(dealersWithTires)

      // Tealium
      if (
        !pageload.current &&
        refineInAction &&
        refineInAction.inProgress === true &&
        setRefineInAction
      ) {
        trackTealEvent({
          tealium_event: "refinement",
          page_type: "parts service",
          search_results: dealersWithTires?.length,
          refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Miles|${radius}`,
          dealer_code: dealersWithTires?.map(dealer => dealer.DealerCode),
          dealer_name: dealersWithTires?.map(dealer => dealer.Name),
        })
        pageload.current = false
        setRefineInAction({ inProgress: false })
      }
    } else if (
      dealerCtaLink === "parts" &&
      refineInAction &&
      setRefineInAction
    ) {
      const partsDealers = dealers?.filter(dealer => dealer.PartsUrl)
      partsDealers?.length === 0 && handleAutoIncreaseRadius()
      setFilteredDealers(partsDealers)
      // Tealium
      if (!pageload.current && refineInAction.inProgress === true) {
        trackTealEvent({
          tealium_event: "refinement",
          page_type: tealPageData?.page_type,
          search_results: partsDealers?.length,
          refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Miles|${radius}`,
          dealer_code: partsDealers?.map(dealer => dealer.DealerCode),
          dealer_name: partsDealers?.map(dealer => dealer.Name),
        })
        pageload.current = false
        setRefineInAction({ inProgress: false })
      }
    } else {
      setFilteredDealers(dealers)
      // Tealium
      if (
        !pageload.current &&
        refineInAction &&
        refineInAction.inProgress === true &&
        dealers &&
        setRefineInAction
      ) {
        if (tealPageData?.page_type == "parts service") {
          trackTealEvent({
            tealium_event: "refinement",
            page_type: "parts service",
            search_results: dealers.length,
            refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Miles|${radius}`,
            dealer_code: dealers.map(dealer => dealer.DealerCode),
            dealer_name: dealers.map(dealer => dealer.Name),
          })
        } else {
          trackTealEvent({
            tealium_event: "refinement",
            page_type: tealPageData?.page_type,
            refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Miles|${radius}`,
            ...(offerType && { offer_type: offerType }),
            search_results: dealers.length,
            dealer_code: dealers.map(dealer => dealer.DealerCode),
            dealer_name: dealers.map(dealer => dealer.Name),
            ...(tealPageData.page_type == "lifestyle" && {
              vehicle_category: lifestyleLanderTealData.selectedCategories,
            }),
            ...(carCareTopic && { vehicle_category: [carCareTopic] }),
          })
        }
        pageload.current = false
        setRefineInAction({ inProgress: false })
      }
    }
    dealers === undefined && handleAutoIncreaseRadius()
  }, [dealers])

  useEffect(() => {
    if (dealersLoading === false && dealers?.length === 0) {
      handleAutoIncreaseRadius()
    }
  }, [dealersLoading])

  // Check if zip is confirmed when the DNY component is 25% visible- if zip has not been confirmed show Confirm Zip Modal
  const [{}, dispatchConfirmZip] = useContext(ConfirmZipContext)
  const dealersNearYouRef = useRef(null)
  const zipIsConfirmed = LocalStorageClient.read("confirmedZip")

  const toggleConfirmZipModal = (entries: { isIntersecting: boolean }[]) => {
    const [entry] = entries
    if (entry.isIntersecting) {
      dispatchConfirmZip(openConfirmZipModal())
    } else {
      dispatchConfirmZip(closeConfirmZipModal())
    }
  }
  useEffect(() => {
    if (zipIsConfirmed !== true) {
      // add timeout so that on page load the observer doesn't immediately fire while the page is still loading and scrolling to top of page
      const timeoutId = setTimeout(() => {
        const observer = new IntersectionObserver(toggleConfirmZipModal, {
          threshold: [0.25],
        })
        if (dealersNearYouRef.current) {
          observer.observe(dealersNearYouRef.current)
        }
        return () => {
          observer.disconnect()
        }
      }, 1000) // Adjust the timeout duration as needed

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [zipIsConfirmed])

  // Ensure observer starts after page load
  useEffect(() => {
    const handlePageLoad = () => {
      console.log("load")
      // if (zipIsConfirmed !== true && dealersNearYouRef.current) {
      //   const observer = new IntersectionObserver(toggleConfirmZipModal, {
      //     threshold: [0.25],
      //   })
      //   observer.observe(dealersNearYouRef.current)
      //   return () => {
      //     observer.disconnect()
      //   }
      // }
    }

    window.addEventListener("load", handlePageLoad)
    return () => {
      window.removeEventListener("load", handlePageLoad)
    }
  }, [dealersNearYouRef, zipIsConfirmed])

  // Support needed for marketing campaigns that use urls with trailing slashes & anchor links
  useEffect(() => {
    if (typeof document === "undefined") return
    const element = document.getElementById(`dealersNearYou`)
    if (location?.hash === "#dealersNearYou/" && element) {
      // Built in .scrollIntoView HTML DOM  Method (with smooth behavior)
      // is not supported in Safari - using polyfill plugin as workaround
      scrollIntoView(element, {
        behavior: "smooth",
        inline: "start",
        block: "start",
      })
    }
  }, [])

  // ** Start Pagination Logic ** //

  const [currentPage, setCurrentPage] = useState(1)
  const [dealersPerPage, setDealersPerPage] = useState<number | null>(null)
  const [currentDealers, setCurrentDealers] = useState<DealerType[] | null>(
    null,
  )
  const { height, width } = useViewportSize()
  const { handleNext, handlePrev, handleJumpTo, maxPage } = usePagination(
    filteredDealers,
    dealersPerPage,
    currentPage,
    setCurrentPage,
  )
  const [mobileView, setMobileView] = useState<boolean | null>(null)
  const isMobileView = width < 1024
  const steps: number[] = [25, 50, 100, 250, 500, 600]
  const findNextHigherValue = (value: number): number => {
    const nextValue = steps.find(step => step >= value)
    return nextValue !== undefined ? nextValue : steps[steps.length - 1]
  }

  const calculateDistance = (
    center: { lat: number; lng: number },
    point: { lat: number; lng: number },
  ): number => {
    const earthRadiusInMiles = 3959 // Earth's radius in miles

    // Convert latitude and longitude from degrees to radians
    const lat1 = center.lat * (Math.PI / 180)
    const lon1 = center.lng * (Math.PI / 180)
    const lat2 = point.lat * (Math.PI / 180)
    const lon2 = point.lng * (Math.PI / 180)

    // Differences in coordinates
    const dLat = lat2 - lat1
    const dLon = lon2 - lon1

    // Haversine formula
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2)

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    // Calculate distance
    const distance = earthRadiusInMiles * c

    return Math.round(distance * 100) / 100 // Round to 2 decimal places
  }

  const handleSearchArea = async (center: { lat: number; lng: number }) => {
    try {
      const searchWithRadius = async (
        radius: number = 0.5,
      ): Promise<string | null> => {
        const bbox = [
          center.lng - radius,
          center.lat - radius,
          center.lng + radius,
          center.lat + radius,
        ]

        // Generate points around the perimeter of the search area
        const searchPoints = [
          { lng: center.lng, lat: center.lat }, // center
          { lng: center.lng - radius, lat: center.lat }, // west
          { lng: center.lng + radius, lat: center.lat }, // east
          { lng: center.lng, lat: center.lat - radius }, // south
          { lng: center.lng, lat: center.lat + radius }, // north
          { lng: center.lng - radius, lat: center.lat - radius }, // southwest
          { lng: center.lng + radius, lat: center.lat - radius }, // southeast
          { lng: center.lng - radius, lat: center.lat + radius }, // northwest
          { lng: center.lng + radius, lat: center.lat + radius }, // northeast
        ]

        // Try each point until we find a result
        for (const point of searchPoints) {
          // Try postal codes first
          const postalResponse = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${point.lng},${point.lat}.json?access_token=${process.env.GATSBY_MAPBOX_API_KEY}&types=postcode&limit=1&bbox=${bbox.join(",")}`,
          )

          const postalData = await postalResponse.json()
          if (postalData.features?.length > 0) {
            return postalData.features[0].text
          }

          // Try broader location search
          const locationResponse = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${point.lng},${point.lat}.json?access_token=${process.env.GATSBY_MAPBOX_API_KEY}&types=place,locality,neighborhood,address,district&limit=1&bbox=${bbox.join(",")}`,
          )

          const locationData = await locationResponse.json()
          if (locationData.features?.length > 0) {
            const context = locationData.features[0].context || []
            const postalCode = context.find(item =>
              item.id.startsWith("postcode"),
            )?.text
            if (postalCode) return postalCode

            const nearestLocation = locationData.features[0]
            const finalPostalResponse = await fetch(
              `https://api.mapbox.com/geocoding/v5/mapbox.places/${nearestLocation.center[0]},${nearestLocation.center[1]}.json?access_token=${process.env.GATSBY_MAPBOX_API_KEY}&types=postcode&limit=1`,
            )

            const finalPostalData = await finalPostalResponse.json()
            if (finalPostalData.features?.length > 0) {
              return finalPostalData.features[0].text
            }
          }
        }

        if (radius < 5) {
          return searchWithRadius(radius * 2)
        }

        return null
      }

      const zipCode = await searchWithRadius()

      if (zipCode) {
        // fire refinement event with updated dealer info
        const refinement_value = `${tealPageData?.page_type}|${tealPageData?.page_name}|search this area`
        dealerSearch("refinement", {
          refinement_value,
          dealer_name: dealerData.dealerNames,
          dealer_code: dealerData.dealerCodes,
        })
        // if the nextRadius is less than 100 set it to 100
        if (!radius || radius < 100) {
          dispatch(setRadius(100))
        }
        dispatch(setMapSearchZip(zipCode))
      }
    } catch (error) {
      console.error("Error searching area:", error)
    }
  }

  // Calculate number of dealers per page
  useEffect(() => {
    setMobileView(isMobileView)
    if (!isMobileView && width >= 1280) {
      setDealersPerPage(2)
    } else if (!isMobileView && (width == 1024 || width < 1280)) {
      setDealersPerPage(1)
    } else if (isMobileView) {
      setDealersPerPage(3)
    }
  }, [height, width, isMobileView])

  // Determine dealers displayed for current page
  useEffect(() => {
    if (
      currentDealers?.length == 0 &&
      filteredDealers &&
      filteredDealers?.length !== 0 &&
      dealersPerPage
    ) {
      setCurrentPage(1)
      const indexOfLastPost = currentPage * dealersPerPage
      const indexOfFirstPost = indexOfLastPost - dealersPerPage
      setCurrentDealers(
        filteredDealers?.slice(indexOfFirstPost, indexOfLastPost),
      )
    } else if (
      filteredDealers &&
      filteredDealers?.length > 0 &&
      currentPage &&
      dealersPerPage
    ) {
      const indexOfLastPost = currentPage * dealersPerPage
      const indexOfFirstPost = indexOfLastPost - dealersPerPage
      setCurrentDealers(
        filteredDealers.slice(indexOfFirstPost, indexOfLastPost),
      )
    }
  }, [
    filteredDealers,
    dealersLoading,
    pageload?.current,
    currentPage,
    dealersPerPage,
  ])

  // Paginate to the selected Dealer
  useEffect(() => {
    if (
      filteredDealers &&
      filteredDealers?.length > 0 &&
      selectedDealer &&
      dealersPerPage
    ) {
      //slice dealers by page location in case of select dealer is not on current page
      const slicedDealers = sliceArrayIntoArrays(
        filteredDealers,
        dealersPerPage,
      )
      mobileView && setView(0)

      if (slicedDealers !== undefined) {
        let pageNumOfSelected = null
        if (slicedDealers && selectedDealer) {
          const index = getIndexOfNestedArrays(slicedDealers, selectedDealer)
          if (index !== undefined) {
            pageNumOfSelected = index + 1
          }
        }
        //handle decrease in results
        if (currentPage > maxPage) {
          setCurrentPage(1)
          //navigate to selected dealer page
        } else if (!dealersLoading && pageNumOfSelected) {
          setCurrentPage(pageNumOfSelected)
        } else {
          setCurrentPage(1)
        }
      }
    }
  }, [selectedDealer, filteredDealers])

  //reset pagination if dealers change after initial load
  useEffect(() => {
    if (currentDealers && dealersLoading && !filteredDealers) {
      setCurrentDealers(null)
    }
  }, [dealersLoading])

  // ** End Pagination Logic ** //
  const isLastPage = currentPage === maxPage

  const handleRadiusChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value)
    const snappedValue = findNextHigherValue(newValue)
    dispatch(setRadius(snappedValue))
  }

  const prevValueRef = useRef<number>()
  const handleRangeTealEvent = (e: React.FocusEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value)
    const snappedValue = findNextHigherValue(newValue)
    // If the new value is the same as the previous value, return early
    if (snappedValue === prevValueRef.current) {
      return
    }
    prevValueRef.current = snappedValue
    handleTealRadiusEvent(radius, snappedValue)
  }

  const handleExpandRadius = () => {
    if (radius === 25) {
      dispatch(setRadius(50))
    } else if (radius === 50) {
      dispatch(setRadius(100))
    }
  }
  const { getDealerInfo } = useDealers()
  const dealerData = getDealerInfo()
  const { dealerSearch } = useDealerSearch()
  // Function depends on dealers search data when radius changes
  const handleTealRadiusEvent: (radius: number, value: number) => void = (
    radius,
    value,
  ) => {
    const refinement_value = `${tealPageData?.page_name}|${tealPageData?.page_type}|expand|${value}`
    if (radius === Number(value)) {
      // if the radius doesn't change:
      trackTealEvent({
        tealium_event: "refinement",
        refinement_value,
        dealer_name: dealerData.dealerNames,
        dealer_code: dealerData.dealerCodes,
        search_results: dealerData.searchResults,
        ...(carCareTopic && { vehicle_category: [carCareTopic] }),
      })
    } else {
      // If the radius does change, there is special handling because of state changes:
      dealerSearch("refinement", {
        refinement_value,
        ...(carCareTopic && { vehicle_category: [carCareTopic] }),
      })
    }
  }
  const [state, modalDispatch] = useContext(DisclaimersContext)
  return (
    <article
      css={[tw`pt-8`]}
      id="dealersNearYou"
      aria-label="Dealers Near You Section"
      ref={dealersNearYouRef}
    >
      {/* Top row */}
      <section css={[tw`bg-white shadow-3 lg:(border-b border-gray-200)`]}>
        {/* Heading */}
        <div css={[tw`max-w-desktop mx-auto`]}>
          <h3
            css={[
              tw`text-xl pb-6 tracking-[2.25px] font-light text-center`,
              tw`lg:(text-3xl tracking-widest )`,
            ]}
          >
            {parseDisclaimerBlocks(heading, selection =>
              modalDispatch(toggleDisclaimersModal(selection)),
            )}
          </h3>
        </div>
        {/* Display and change results radius */}
        <div
          css={[
            tw`text-sm ml-5 flex flex-col items-center justify-center gap-y-2 pb-6`,
            tw`lg:(relative text-center justify-center pb-0 gap-y-0 top-0)`,
          ]}
        >
          <div css={[tw`mt-0 min-w-[300px]`]}>
            {/* <span css={[tw`font-semibold mr-1.5`]}>
              {filteredDealers?.length || 0}
            </span>
            <span>
              {_("Dealers within")}{" "}
              {radius !== 600
                ? `${radius} ${_("miles")}`
                : ` >500 ${_("miles")}`}
            </span>
            <input
              type="range"
              min="25"
              max="600"
              onChange={handleRadiusChange}
              onBlur={handleRangeTealEvent}
              value={radius}
              tw="appearance-none w-full h-1 bg-gray-200 rounded-full cursor-pointer transition-colors duration-200 ease-in-out
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-red-500
                  [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-red-500"
            /> */}
            <div>
              <RadiusSelector fromInventory={false} />
            </div>
          </div>
          <span css={tw`mr-2 mt-3`}>{_(" ")}</span>
          <span css={tw`max-w-[22rem] mt-3 lg:mb-8`}>
            <LocationSelector inputOnly={true} analyticsId={"location"} />
          </span>
        </div>

        {/* Tabs - Change view state */}
        <div
          css={[tw`flex justify-center gap-10 mt-5 text-xs`, tw`lg:(hidden)`]}
        >
          <button
            onClick={() => setView(0)}
            css={[
              tw`font-semibold border-b-4 border-white p-1 uppercase text-sm`,
              view === 0 && activeStyle,
            ]}
          >
            {_("LIST VIEW")}
          </button>
          <button
            onClick={() => setView(1)}
            css={[
              tw`font-semibold border-b-4 border-white p-1 uppercase text-sm`,
              view === 1 && activeStyle,
            ]}
          >
            {_("MAP VIEW")}
          </button>
        </div>
      </section>

      {/* Main body */}
      <section
        css={[
          filteredDealers === undefined || filteredDealers?.length === 0
            ? tw`bg-none relative shadow-5 lg:(grid grid-cols-[100%])`
            : tw`bg-none relative shadow-5 lg:(grid grid-cols-[40% 60%])`,
        ]}
      >
        <ConfirmZipModal fromDealersNearYou />
        {/* List view */}
        {/* Visible according to tab state or when browser is > lg width */}
        <div
          css={[
            css`
              @media (min-width: 1024px) {
                max-height: ${mapHeight}px;
                ${tw`visible bg-gray-100 flex flex-col h-full  pt-4 scrollbar-hide`}
              }
            `,
            view === 0
              ? css`
                  ${tw`flex flex-col overflow-y-hidden pt-4`}
                `
              : tw`hidden h-full`,
          ]}
        >
          {filteredDealers === undefined || filteredDealers?.length === 0 ? (
            <div
              css={[
                `height: ${mapHeight}px;`,
                tw`flex justify-center items-center w-full mx-auto text-center overflow-visible -mt-4 h-[400px] bg-gray-50 opacity-50`,
                tw`lg:(hidden)`,
                `background-image: url(${process.env.GATSBY_ROOT_URL}map-bg.png)`,
                `background-repeat: no-repeat;`,
                `background-position:center;`,
                `@media only screen and (max-width: 600px)  {background-size: contain;}`,
              ]}
            >
              <div css={[tw`-mt-20 w-4/5`, tw`md:(w-2/3)`, tw`lg:(w-1/3)`]}>
                {_(
                  "Sorry, we couldn't find any dealers in your selected area. ",
                )}
                {_(
                  "Please try expanding your search area, or enter a new location.",
                )}
              </div>
            </div>
          ) : (
            <div css={[tw`h-full flex flex-col pb-4`]}>
              {currentDealers?.map((dealer, i) => (
                <Card
                  key={dealer?.Id}
                  shadowLevel={
                    selectedDealer && dealer?.Id === selectedDealer?.Id ? 5 : 3
                  }
                  css={[
                    tw`bg-white mb-2 md:(mx-4) min-h-[250px] border-2 border-transparent`,
                    selectedDealer &&
                      dealer?.Id === selectedDealer?.Id &&
                      tw`border-red-400`,
                  ]}
                >
                  {pilotDealers ? (
                    <PilotDealer
                      dealer={dealer}
                      i={filteredDealers?.indexOf(dealer) + 1}
                      onClick={() => dispatch(setSelectedDealer(dealer?.Id))}
                      collapsable={false}
                      dealerCtaText={dealerCtaText}
                      dealerCtaLink={dealerCtaLink}
                      ctaAnalyticsId={ctaAnalyticsId}
                      offerType={offerType}
                      pilotPhoneNumber={pilotPhoneNumber}
                    />
                  ) : (
                    <Dealer
                      dealer={dealer}
                      i={filteredDealers?.indexOf(dealer) + 1}
                      onClick={() => {
                        dispatch(setSelectedDealer(dealer?.Id))
                      }}
                      collapsable={false}
                      dealerCtaText={dealerCtaText}
                      dealerCtaLink={dealerCtaLink}
                      ctaAnalyticsId={ctaAnalyticsId}
                      offerType={offerType}
                      carCareTopic={carCareTopic}
                      excludeCallDealerPages={[
                        "/parts/tires/",
                        "/parts/",
                        "/toyota-careers/technician-program/",
                      ]}
                    />
                  )}
                </Card>
              ))}
              <div css={[tw`flex content-end justify-center items-center`]}>
                <Paginate
                  itemsPerPage={dealersPerPage}
                  totalDealers={dealers?.length || null}
                  previousPage={handlePrev}
                  nextPage={handleNext}
                  currentPage={currentPage}
                  handleJumpTo={(page: number) => handleJumpTo(page)}
                  radius={radius}
                  maxPage={maxPage}
                />

                {isLastPage && (radius === 25 || radius === 50) && (
                  <button
                    onClick={() => {
                      handleExpandRadius()
                      handleTealRadiusEvent(radius, value)
                    }}
                    analytics-id={`expand filter:location:${
                      radius === 25 ? 1 : 2
                    }`}
                    css={[
                      tw`w-44 h-8 bg-white my-4 flex-nowrap whitespace-nowrap content-center rounded-md normal-case font-semibold flex justify-center items-center text-sm shadow-2 cursor-pointer hover:(bg-red-400 text-white)`,
                    ]}
                  >
                    {radius === 25
                      ? _("Expand to 50 miles")
                      : _("Expand to 100 miles")}
                    <Icon.Chevron css={[tw`ml-1 mt-1 h-2 w-2`]} />
                  </button>
                )}
              </div>
              <div css={[tw`flex justify-center font-semibold`]}>
                {filteredDealers?.length} {_("Dealers within")}{" "}
                {radius !== 600 ? `${radius} ${_("miles")}` : ` >500`}{" "}
                {_("Miles")}
              </div>
            </div>
          )}
        </div>

        {/* Map view */}
        {/* Visible according to tab state or when browser is > lg width */}
        <div
          css={[
            tw`invisible max-h-0`,
            `@media (min-width: 1024px) {
                visibility: visible;
                max-height: ${mapHeight}px;
              }`,
            view === 1 && `visibility: visible; max-height: ${mapHeight}px;`,
          ]}
        >
          <div css={[tw`sticky`, `top: ${mapOffset}px;`]}>
            {dealersLoading && (
              <div
                css={[
                  tw`absolute top-0 left-0 right-0 bottom-0 bg-gray-400 z-10 bg-opacity-70 flex items-center justify-center`,
                ]}
              >
                <div>
                  <Icon.Loader
                    color="red-400"
                    css={[tw`max-h-32 animate-spin mx-auto`]}
                  />
                  <span
                    css={[
                      tw`block mt-4 text-lg text-gray-700 font-bold text-center leading-tight uppercase`,
                    ]}
                  >
                    {_("FINDING DEALERS")}...
                  </span>
                </div>
              </div>
            )}
            {filteredDealers === undefined || dealers?.length === 0 ? (
              <div
                css={[
                  `height: ${mapHeight}px;`,
                  tw`flex justify-center items-center w-full mx-auto text-center overflow-visible bg-gray-50`,
                  `background-image: url(${process.env.GATSBY_ROOT_URL}map-bg.png)`,
                  `background-repeat: no-repeat;`,
                  `background-position:center;`,
                  `@media only screen and (max-width: 600px)  {background-size: contain;}`,
                ]}
              >
                <div css={[tw`-mt-20 w-4/5`, tw`md:(w-2/3)`, tw`lg:(w-1/3)`]}>
                  {_(
                    "Sorry, we couldn't find any dealers in your selected area. ",
                  )}
                  {_(
                    "Please try expanding your search area, or enter a new location.",
                  )}
                </div>
              </div>
            ) : (
              <DealerMap
                center={latLng}
                dealers={filteredDealers}
                allowPan={true}
                radius={radius}
                mapContainerStyle={{
                  width: "100%",
                  height: `${mapHeight}px`,
                }}
                onSearchArea={handleSearchArea}
              />
            )}
          </div>
        </div>
      </section>
    </article>
  )
}

export default DealersNearYou
